/**
 * CogoToolRegistry — central registry for WebMCP tools.
 *
 * Each tool has a name, description, input/output JSON Schema,
 * and an async handler. The registry supports listener notification
 * on changes, manifest export, and thread-safe tool invocation.
 */

import { validateInput } from "./schema-validator"

// ── Types ──────────────────────────────────────────────────

export interface JsonSchema {
  type?: string | readonly string[]
  properties?: Record<string, JsonSchema>
  required?: string[]
  items?: JsonSchema
  enum?: unknown[]
  description?: string
  default?: unknown
  anyOf?: JsonSchema[]
  minLength?: number
  minItems?: number
  minProperties?: number
  [key: string]: unknown
}

export interface WebMCPToolMetadata {
  /** Origin block that exposed this tool */
  blockName?: string
  /** Whether the tool only reads state (no mutations) */
  readOnly?: boolean
  /** User-confirmation policy for this tool */
  confirmation?: 'never' | 'when-mutating' | 'always'
  /** Runtime that is authoritative for applying and reading back the result */
  executionAuthority?: 'browser' | 'studio' | 'server' | 'runtime'
  /** Retry policy. Mutating tools default to never unless explicitly idempotent. */
  retryPolicy?: 'never' | 'safe'
  /** Input property carrying the idempotency key for a safely retryable mutation */
  idempotencyKeyParam?: string
  /** Per-tool timeout. The agent forwards an AbortSignal when this expires. */
  timeoutMs?: number
  /** Arbitrary tags for filtering */
  tags?: string[]
}

export interface WebMCPToolInvocationContext {
  readonly signal?: AbortSignal
}

export interface WebMCPTool {
  name: string
  description: string
  inputSchema: JsonSchema
  outputSchema?: JsonSchema
  handler: (
    params: Record<string, unknown>,
    context?: WebMCPToolInvocationContext,
  ) => unknown | Promise<unknown>
  metadata?: WebMCPToolMetadata
}

export interface ToolManifestEntry {
  name: string
  description: string
  inputSchema: JsonSchema
  outputSchema?: JsonSchema
  metadata?: WebMCPToolMetadata
}

export type ToolsChangedListener = (tools: ToolManifestEntry[]) => void

// ── Registry ───────────────────────────────────────────────

export class CogoToolRegistry {
  private tools = new Map<string, WebMCPTool>()
  private listeners = new Set<ToolsChangedListener>()
  private manifestCache: ToolManifestEntry[] | null = null

  /** Register a tool. Overwrites any existing tool with the same name. */
  register(tool: WebMCPTool): void {
    this.tools.set(tool.name, tool)
    this.manifestCache = null
    this.notifyListeners()
  }

  /** Remove a tool by name. Returns true if it existed. */
  unregister(name: string): boolean {
    const existed = this.tools.delete(name)
    if (existed) {
      this.manifestCache = null
      this.notifyListeners()
    }
    return existed
  }

  /** Get a tool by name, or undefined if not registered. */
  getTool(name: string): WebMCPTool | undefined {
    return this.tools.get(name)
  }

  /** Get all registered tools. */
  getTools(): WebMCPTool[] {
    return Array.from(this.tools.values())
  }

  /**
   * Invoke a tool by name with the given params.
   * Throws if the tool is not registered.
   */
  async callTool(
    name: string,
    params: Record<string, unknown>,
    context?: WebMCPToolInvocationContext,
  ): Promise<unknown> {
    const tool = this.tools.get(name)
    if (!tool) {
      throw new Error(`WebMCP tool not found: "${name}"`)
    }
    if (tool.inputSchema) {
      const result = validateInput(tool.inputSchema, params)
      if (!result.valid) {
        throw new Error(
          `WebMCP tool "${name}" input invalid: ` +
          result.errors.map((e) => `${e.path}: ${e.message}`).join("; "),
        )
      }
    }
    if (context?.signal?.aborted) {
      throw context.signal.reason ?? new DOMException("Aborted", "AbortError")
    }
    return context === undefined
      ? tool.handler(params)
      : tool.handler(params, context)
  }

  /** Get W3C-compatible manifest (tool definitions without handlers). */
  getManifest(): ToolManifestEntry[] {
    if (!this.manifestCache) {
      this.manifestCache = Array.from(this.tools.values()).map((t) => ({
        name: t.name,
        description: t.description,
        inputSchema: t.inputSchema,
        outputSchema: t.outputSchema,
        metadata: t.metadata,
      }))
    }
    return this.manifestCache
  }

  /** Subscribe to tools-changed events. Returns an unsubscribe function. */
  onToolsChanged(listener: ToolsChangedListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /** Remove all tools and notify listeners. */
  clear(): void {
    if (this.tools.size === 0) return
    this.tools.clear()
    this.manifestCache = null
    this.notifyListeners()
  }

  /** Number of registered tools. */
  get size(): number {
    return this.tools.size
  }

  private notifyListeners(): void {
    if (this.listeners.size === 0) return
    const manifest = this.getManifest()
    for (const listener of this.listeners) {
      try {
        listener(manifest)
      } catch {
        // Listener errors should not break the registry
      }
    }
  }
}
